import { useEffect, useRef } from "react";
import { useLocation } from "wouter";
import { getSupabaseClient, isHttpOnlyRefreshEnabled } from "@/lib/supabase/client";
import { adoptRefreshToken, proxyLogout, PROXY_REFRESH_PLACEHOLDER } from "@/lib/supabase/httpOnlyProxy";
import { getPlatformConfig } from "@/lib/platform/config";
import { Spinner } from "@/components/ui/spinner";
import { classifyOAuthError, type OAuthErrorCode } from "./oauth-error";
import { postAuthWouterDestination, safeRedirectPath } from "@/lib/safeRedirect";
import { useAppTranslation } from "@/i18n/useAppTranslation";
import { POST_AUTH_REDIRECT_KEY } from "./AuthModal";

export default function AuthCallbackPage() {
  const [, setLocation] = useLocation();
  const processed = useRef(false);
  const { t } = useAppTranslation();

  useEffect(() => {
    if (processed.current) return;
    processed.current = true;

    const supabase = getSupabaseClient();
    if (!supabase) {
      setLocation("/login?error=Supabase+not+configured");
      return;
    }

    const url = new URL(window.location.href);
    const code = url.searchParams.get("code");
    const appleLinkIntent = url.searchParams.get("apple_link_intent");
    // Supabase / OAuth providers return error info via either query params or
    // hash fragment depending on flow type. Surface either back to /login.
    const hashParams = new URLSearchParams(url.hash.startsWith("#") ? url.hash.slice(1) : "");
    const oauthError =
      url.searchParams.get("error") ||
      url.searchParams.get("error_code") ||
      hashParams.get("error") ||
      hashParams.get("error_code");
    const oauthErrorDescription =
      url.searchParams.get("error_description") ||
      hashParams.get("error_description");

    const ensureUserViaApi = async (accessToken: string): Promise<{ isNew: boolean; isAdmin: boolean; isOwner: boolean; softDeleted: boolean }> => {
        const config = getPlatformConfig();
        const apiBase = import.meta.env.BASE_URL || "/";
        const res = await fetch(`${apiBase}api/auth/ensure-user`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          // Task #5287 — WEB defers public.users creation until the
          // profile-completion dialog submits name + Terms. A brand-new OAuth
          // identity comes back account_status:"needs_profile" (created:false)
          // and is routed home, where the globally-mounted completion dialog
          // captures name + Terms and creates the row. Existing complete users
          // are unaffected (ensure-user returns their row regardless).
          body: JSON.stringify({ platform_origin: config.platform, defer_creation: true }),
        });
        if (!res.ok) {
          const body = await res.json().catch(() => null) as { code?: unknown } | null;
          const serverCode = typeof body?.code === "string" ? body.code : "profile_setup_failed";
          const classified = classifyOAuthError(serverCode);
          if (
            classified === "existing_account_link_required" ||
            classified === "identity_already_linked" ||
            classified === "email_collision"
          ) {
            // The provider exchange has already established a Supabase session.
            // It is not safe to retain that unbound/conflicting principal while
            // asking the person to recover with their existing email account.
            await Promise.allSettled([
              supabase.auth.signOut({ scope: "local" }),
              proxyLogout(null),
            ]);
          }
          const error = new Error("profile_setup_failed") as Error & { authCode?: OAuthErrorCode };
          error.authCode = classified;
          throw error;
        }
        const json = await res.json();
        return {
          isNew: json.created === true,
          isAdmin: (json.profile?.admin_role ?? 0) > 0,
          isOwner: !!json.is_owner,
            // Task #2451 — server returns account_status="soft_deleted"
            // when this OAuth identity maps to an account inside the
            // 30-day GDPR retention window. Route the callback to the
            // home page (not /?welcome=1) so AuthProvider's
            // SoftDeletedAutoOpener can surface the restore UX.
          softDeleted: json.account_status === "soft_deleted",
        };
    };

    const resolveRedirect = (result: { isNew: boolean; isAdmin: boolean; isOwner: boolean; softDeleted: boolean }, explicitNext: string) => {
      if (result.softDeleted) return "/";
      if (explicitNext !== "/") return explicitNext;
      if (result.isNew) return "/?welcome=1";
      if (result.isAdmin) return "/admin";
      if (result.isOwner) return "/dashboard";
      return "/";
    };

    // Read `next` from the URL param first (OAuth / magic-link flows that pass
    // it explicitly). For the password-reset path the recovery link lands on
    // /auth/callback?code=... with NO `next` param, but the user was mid-
    // session on a protected page (e.g. /messages/<uuid>) that stashed the
    // destination in sessionStorage via rememberPostAuthNext. Fall through to
    // that key before defaulting to "/", so the reset flow also lands the user
    // on the page they started from. The value is consumed (cleared) here so
    // PostAuthRedirector doesn't fire a second redundant navigation.
    const nextFromUrl = url.searchParams.get("next");
    let sessionStorageNext: string | null = null;
    try {
      // URL intent wins, but always consume the backup: leaving it behind
      // would allow a second navigation from the global profile redirector.
      sessionStorageNext = sessionStorage.getItem(POST_AUTH_REDIRECT_KEY);
      sessionStorage.removeItem(POST_AUTH_REDIRECT_KEY);
    } catch { /* storage can be unavailable; the URL still carries next */ }
    const explicitNext = safeRedirectPath(nextFromUrl ?? sessionStorageNext);

    const buildLoginRedirect = (errorCode: OAuthErrorCode) => {
      const params = new URLSearchParams();
      params.set("auth_error", errorCode);
      if (explicitNext) params.set("next", explicitNext);
      return `/login?${params.toString()}`;
    };

    const appleLinkRedirect = (state: string) =>
      `/settings?section=security&apple_link=${encodeURIComponent(state)}`;

    const finalizeAppleLink = async (accessToken: string) => {
      const res = await fetch(`${import.meta.env.BASE_URL || "/"}api/auth/apple-link-intent/finalize`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify({ intent: appleLinkIntent }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null) as { code?: unknown } | null;
        const code = typeof body?.code === "string" ? body.code : "provider_error";
        setLocation(appleLinkRedirect(code));
        return;
      }
      // The API verifies the identity server-side. Refresh the client user as
      // well so Settings renders the connected state immediately.
      const { data: { user }, error } = await supabase.auth.getUser();
      if (error || !user?.identities?.some((identity) => identity.provider === "apple")) {
        setLocation(appleLinkRedirect("apple_identity_missing"));
        return;
      }
      setLocation(appleLinkRedirect("success"));
    };

    // Resolve the post-auth destination and navigate. Login lands at aal1;
    // two-factor is enforced at the admin panel (AdminMfaGate + server
    // requireAdmin), not here, so an admin is simply routed to /admin and
    // steps up at the gate.
    const finishAndRedirect = async (accessToken: string) => {
      const result = await ensureUserViaApi(accessToken);
      setLocation(postAuthWouterDestination(resolveRedirect(result, explicitNext)));
    };

    const handleCallback = async () => {
      try {
        if (oauthError) {
          if (appleLinkIntent) {
            setLocation(appleLinkRedirect("provider_error"));
            return;
          }
          setLocation(buildLoginRedirect(classifyOAuthError(oauthError, oauthErrorDescription)));
          return;
        }
        if (code) {
          const { data, error } = await supabase.auth.exchangeCodeForSession(code);
          if (error) {
            setLocation(buildLoginRedirect(classifyOAuthError(
              (error as { code?: string }).code ?? error.name,
              error.message,
            )));
            return;
          }
          if (data.session) {
            // Task #3065 — before anything else can observe the
            // refresh token, hand it to the HttpOnly-cookie proxy
            // and strip it from the Supabase client. AuthProvider
            // will then continue refreshing through the proxy.
            if (isHttpOnlyRefreshEnabled()) {
              try {
                if (!data.session.refresh_token) throw new Error();
                await adoptRefreshToken(data.session.refresh_token);
                const { data: proxySession, error: setError } = await supabase.auth.setSession({
                  access_token: data.session.access_token,
                  refresh_token: PROXY_REFRESH_PLACEHOLDER,
                });
                if (setError || !proxySession?.session) throw setError ?? new Error();
              } catch (err) {
                console.warn("[AuthCallback] secure session setup failed", err);
                // The code exchange has already signed the client in. Clear
                // that partial state (and any cookie set before setSession
                // failed) before offering another sign-in attempt.
                await Promise.allSettled([
                  supabase.auth.signOut({ scope: "local" }),
                  proxyLogout(null),
                ]);
                setLocation(buildLoginRedirect("callback_failed"));
                return;
              }
            }
            if (appleLinkIntent) {
              await finalizeAppleLink(data.session.access_token);
            } else {
              await finishAndRedirect(data.session.access_token);
            }
            return;
          }
          setLocation(postAuthWouterDestination(appleLinkIntent ? appleLinkRedirect("apple_identity_missing") : explicitNext));
        } else {
          const { data: { session }, error } = await supabase.auth.getSession();
          if (error || !session) {
            setLocation(buildLoginRedirect(classifyOAuthError(
              (error as { code?: string } | null)?.code,
              error?.message,
            )));
            return;
          }
          if (appleLinkIntent) {
            await finalizeAppleLink(session.access_token);
          } else {
            await finishAndRedirect(session.access_token);
          }
        }
      } catch (err) {
        if (appleLinkIntent) {
          setLocation(appleLinkRedirect("provider_error"));
          return;
        }
        const authCode =
          err && typeof err === "object" && "authCode" in err
            ? (err as { authCode?: OAuthErrorCode }).authCode
            : undefined;
        setLocation(buildLoginRedirect(authCode ?? "profile_setup_failed"));
      }
    };

    handleCallback();
  }, [setLocation, t]);

  return (
    <div className="flex min-h-[60vh] items-center justify-center">
      <div className="text-center">
        <Spinner size="lg" className="mx-auto text-primary" />
        <p className="mt-4 text-sm text-muted-foreground">{t("auth.completing_sign_in", { defaultValue: "Completing sign in..." })}</p>
      </div>
    </div>
  );
}
